/**
 * HumanFirst.chat Widget Loader
 * Lightweight bootstrap that checks enabled state before loading the core bundle.
 */

import type {
  HFChatAPI,
  HFChatConfig,
  HFChatIdentifyMeta,
  HFChatWidgetSettings,
} from "@chat/shared";

interface WidgetConfig {
  siteId: string;
  siteName: string;
  enabled?: boolean;
  primaryColor: string;
  position: "left" | "right";
  size: "xs" | "small" | "medium" | "large";
  greeting?: string;
  placeholder?: string;
  launcherStyle?: "bubble" | "card" | "humanfirst";
  windowStyle?: "clean" | "soft" | "line" | "glass";
  launcherName?: string;
  launcherDescription?: string;
  launcherImageAvifUrl?: string;
  launcherImageAvif2xUrl?: string;
  launcherImageWebpUrl?: string;
  launcherImageWebp2xUrl?: string;
  launcherCardWidth?: number | null;
  launcherCardHeight?: number | null;
  launcherBadgeStyle?: "text" | "icon" | "none";
  launcherBadgeIcon?:
    | "chat"
    | "sparkles"
    | "headset"
    | "phone"
    | "mail"
    | "help"
    | "smile"
    | "zap"
    | "heart"
    | "star";
  visitorDraftPreviewEnabled?: boolean;
  poweredByEnabled?: boolean;
  operatorOnline?: boolean;
  widgetUrl?: string;
  sockudoHost?: string;
  sockudoKey?: string;
}

type QueuedCall = {
  method: keyof HFChatAPI;
  args: unknown[];
};

type HFChatStub = HFChatAPI & { __hfchatStub?: true; __HFCHAT_STUB__?: true; __real?: HFChatAPI };

/** The widget core marks its instance, as opposed to this loader's stub. */
type HFChatInstance = HFChatAPI & { __HFCHAT_INSTANCE__?: boolean };
type HFChatRealInstance = HFChatAPI & { __HFCHAT_INSTANCE__: true };

function isRealInstance(value: HFChatInstance | null | undefined): value is HFChatRealInstance {
  return value?.__HFCHAT_INSTANCE__ === true;
}

declare global {
  interface Window {
    HFChat?: HFChatAPI;
    HumanFirstChat?: {
      init: (opts: unknown) => HFChatAPI;
      initAsync?: (opts: unknown) => Promise<HFChatAPI>;
    };
    __HFCHAT_BOOTSTRAP__?: {
      widgetConfig?: WidgetConfig;
      siteId?: string;
      baseURL?: string;
      host?: string;
      resumeToken?: string;
    };
    __HFCHAT_QUEUE__?: QueuedCall[];
    __HFCHAT_REAL__?: HFChatAPI;
  }
}

const DEFAULT_BASE_URL = "https://humanfirst.chat";
const CORE_SCRIPT_NAME = "widget-core.js";
const RESUME_QUERY_PARAM = "hfchat_resume";

/**
 * Read and remove `#hfchat_resume=` from the host page URL: the link of a follow-up e-mail
 * reopens the visitor's conversation in this browser. The token is a credential, so it leaves
 * the address bar synchronously, before anything reads `location.href`, and it is handed to the
 * widget core through the bootstrap only.
 */
function takeResumeToken(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const url = new URL(window.location.href);
    const fragment = new URLSearchParams(url.hash.slice(1));
    const token = fragment.get(RESUME_QUERY_PARAM) ?? url.searchParams.get(RESUME_QUERY_PARAM);
    if (!token) return undefined;
    url.searchParams.delete(RESUME_QUERY_PARAM);
    if (fragment.has(RESUME_QUERY_PARAM)) {
      fragment.delete(RESUME_QUERY_PARAM);
      url.hash = fragment.toString();
    }
    window.history.replaceState(window.history.state, "", url.toString());
    return token;
  } catch {
    return undefined;
  }
}

function getScriptElement(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;
  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.src?.includes("widget.js")) return current;
  const scripts = document.querySelectorAll('script[src*="widget.js"]');
  return scripts[scripts.length - 1] as HTMLScriptElement | null;
}

function detectBaseURL(): string {
  if (typeof document === "undefined") return DEFAULT_BASE_URL;
  const script = getScriptElement();
  if (script?.src) {
    try {
      const scriptURL = new URL(script.src);
      return `${scriptURL.protocol}//${scriptURL.host}`;
    } catch {
      return DEFAULT_BASE_URL;
    }
  }
  return DEFAULT_BASE_URL;
}

function log(logs: HFChatConfig["logs"], message: string, ...args: unknown[]) {
  if (logs === "none") return;
  console.log(`[HumanFirst Chat Widget] ${message}`, ...args);
}

function logError(logs: HFChatConfig["logs"], message: string, ...args: unknown[]) {
  if (logs === "none") return;
  console.error(`[HumanFirst Chat Widget] ${message}`, ...args);
}

function getQueue(): QueuedCall[] {
  if (typeof window === "undefined") return [];
  if (!window.__HFCHAT_QUEUE__) {
    window.__HFCHAT_QUEUE__ = [];
  }
  return window.__HFCHAT_QUEUE__;
}

function enqueue(method: keyof HFChatAPI, args: unknown[]) {
  getQueue().push({ method, args });
}

function getRealApi(): HFChatAPI | null {
  if (typeof window === "undefined") return null;

  const globalReal = window.__HFCHAT_REAL__;
  if (isRealInstance(globalReal)) {
    return globalReal;
  }

  const candidate: HFChatStub | undefined = window.HFChat;
  if (isRealInstance(candidate)) {
    return candidate;
  }

  const real = candidate?.__real;
  if (isRealInstance(real)) {
    return real;
  }

  return null;
}

function callOrQueue(method: keyof HFChatAPI, args: unknown[]) {
  const api = getRealApi();
  const fn = api ? api[method] : null;

  if (api && typeof fn === "function") {
    (fn as (...innerArgs: unknown[]) => void).apply(api, args);
    return;
  }

  enqueue(method, args);
}

function createStubApi(): HFChatAPI {
  const stub: HFChatStub = {
    show: () => callOrQueue("show", []),
    hide: () => callOrQueue("hide", []),
    open: (options) => callOrQueue("open", [options]),
    close: () => callOrQueue("close", []),
    toggle: () => callOrQueue("toggle", []),
    isOpen: () => {
      const api = getRealApi();
      return api?.isOpen?.() ?? false;
    },
    reset: () => callOrQueue("reset", []),
    identify: (meta: HFChatIdentifyMeta) => callOrQueue("identify", [meta]),
    configure: (settings: HFChatWidgetSettings) => callOrQueue("configure", [settings]),
    onReady: (callback: () => void) => {
      if (typeof callback !== "function") return;
      const api = getRealApi();
      if (api?.ready) {
        try {
          callback();
        } catch (error) {
          console.error("[HumanFirst Chat Widget] Error in onReady callback:", error);
        }
        return;
      }
      callOrQueue("onReady", [callback]);
    },
    onOpen: (callback: () => void) => {
      if (typeof callback !== "function") return;
      callOrQueue("onOpen", [callback]);
    },
    onClose: (callback: () => void) => {
      if (typeof callback !== "function") return;
      callOrQueue("onClose", [callback]);
    },
    get ready() {
      const api = getRealApi();
      return api?.ready ?? false;
    },
  };
  stub.__hfchatStub = true;
  (stub as HFChatStub & { __HFCHAT_STUB__?: true }).__HFCHAT_STUB__ = true;
  return stub;
}

function flushQueue(api: HFChatAPI) {
  const queue = getQueue();
  if (!queue.length) return;
  queue.splice(0).forEach(({ method, args }) => {
    const fn = api[method];
    if (typeof fn === "function") {
      (fn as (...innerArgs: unknown[]) => void).apply(api, args);
    }
  });
}

function attachRealInstance(api: HFChatAPI) {
  if (typeof window === "undefined") return;

  window.__HFCHAT_REAL__ = api;

  const existing = window.HFChat as (HFChatStub & { __HFCHAT_INSTANCE__?: boolean }) | undefined;
  if (existing && existing.__HFCHAT_STUB__ === true) {
    existing.__real = api;
    return;
  }

  window.HFChat = api;
}

/**
 * Undo `attachRealInstance` after a failed initialization, so that a later `init()` starts
 * from a clean page: stub restored, iframe container removed.
 */
function detachFailedInstance() {
  const api = getRealApi();
  if (!api || api.ready) return;

  delete window.__HFCHAT_REAL__;
  const current = window.HFChat as HFChatStub | undefined;
  if (current?.__HFCHAT_STUB__ === true) {
    delete current.__real;
  } else if (current === api) {
    window.HFChat = createStubApi();
  }
  document.getElementById("hfchat-widget-container")?.remove();
}

async function fetchWidgetConfig(
  config: HFChatConfig & { baseURL: string },
): Promise<WidgetConfig> {
  const websiteHost =
    config.host || (typeof window !== "undefined" ? window.location.hostname : "");

  if (config.siteId) {
    const url = `${config.baseURL}/api/widget/config?siteId=${config.siteId}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch widget config: ${response.status}`);
    }
    return response.json();
  }

  if (websiteHost === "localhost" || websiteHost === "127.0.0.1") {
    throw new Error("siteId is required on localhost");
  }

  const url = `${config.baseURL}/api/widget/site-settings?host=${encodeURIComponent(websiteHost)}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to resolve site: ${response.status}`);
  }

  const data = await response.json();
  return data.siteSettings ?? data;
}

function loadCoreScript(baseURL: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Cannot load widget core in non-browser environment"));
      return;
    }

    const existing = document.querySelector(
      'script[data-hfchat-core="1"]',
    ) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => {
            // Drop the failed tag so that a later init() can load the core again
            existing.remove();
            reject(new Error("Failed to load widget core"));
          },
          { once: true },
        );
      }
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `${baseURL}/${CORE_SCRIPT_NAME}`;
    script.setAttribute("data-hfchat-core", "1");
    script.setAttribute("data-auto-init", "false");
    script.onerror = () => {
      script.remove();
      reject(new Error("Failed to load widget core"));
    };
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    document.head.appendChild(script);
  });
}

let initPromise: Promise<HFChatAPI> | null = null;

const READY_TIMEOUT_MS = 15_000;

function waitForReady(api: HFChatAPI): Promise<HFChatAPI> {
  if (api.ready) return Promise.resolve(api);
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error("Widget did not become ready within 15 seconds")),
      READY_TIMEOUT_MS,
    );
    api.onReady(() => {
      clearTimeout(timeout);
      resolve(api);
    });
  });
}

/**
 * Synchronous entry point, kept for direct script embeds and the published npm SDK: returns
 * the stub (or the real instance once attached) and starts the load in the background.
 * Use `initAsync()` to await the ready widget or observe a failure.
 */
export function init(opts?: unknown): HFChatAPI {
  if (typeof window === "undefined") {
    throw new Error("HFChat can only be initialized in a browser environment");
  }

  if (!window.HFChat) {
    window.HFChat = createStubApi();
  }

  const existingApi = getRealApi();
  if (existingApi) return window.HFChat;

  if (initPromise) {
    return window.HFChat;
  }

  const resolvedOpts = opts && typeof opts === "object" ? (opts as HFChatConfig) : {};
  const baseURL = resolvedOpts.baseURL || detectBaseURL();
  const config: HFChatConfig & { baseURL: string } = {
    baseURL,
    logs: resolvedOpts.logs || "none",
    siteId: resolvedOpts.siteId,
    hidden: resolvedOpts.hidden,
    hideOnClose: resolvedOpts.hideOnClose,
    host: resolvedOpts.host,
  };
  // Taken now, before the first network round trip, so that the URL is clean at once.
  const resumeToken = takeResumeToken();

  initPromise = (async () => {
    const widgetConfig = await fetchWidgetConfig(config);
    const resolvedEnabled = widgetConfig.enabled ?? true;

    if (!resolvedEnabled) {
      log(config.logs, "Widget disabled by site settings");
      throw new Error("Widget is disabled by site settings");
    }

    window.__HFCHAT_BOOTSTRAP__ = {
      widgetConfig,
      siteId: widgetConfig.siteId,
      baseURL,
      host: config.host,
      ...(resumeToken ? { resumeToken } : {}),
    };

    log(config.logs, "Loading widget-core script...");
    // The loader and the core share the global name `HumanFirstChat`: a freshly loaded core
    // must replace the loader's object, otherwise the script did not run (wrong response).
    const coreAlreadyLoaded =
      document.querySelector<HTMLScriptElement>('script[data-hfchat-core="1"]')?.dataset.loaded ===
      "true";
    const loaderGlobal = window.HumanFirstChat;
    await loadCoreScript(baseURL);
    log(config.logs, "Widget-core script loaded");

    if (
      (!coreAlreadyLoaded && window.HumanFirstChat === loaderGlobal) ||
      !window.HumanFirstChat?.init
    ) {
      document.querySelector('script[data-hfchat-core="1"]')?.remove();
      throw new Error("HumanFirstChat.init is not available after core load");
    }

    log(config.logs, "Calling widget-core init...");
    const api = window.HumanFirstChat.init({
      siteId: widgetConfig.siteId,
      hidden: config.hidden,
      hideOnClose: config.hideOnClose,
      logs: config.logs,
      baseURL: config.baseURL,
      host: config.host,
    });

    log(config.logs, "Widget-core init returned:", {
      hasInstance: isRealInstance(api),
    });
    attachRealInstance(api);
    log(config.logs, "Real instance attached");
    flushQueue(api);
    return waitForReady(api);
  })().catch((error: unknown) => {
    // Leave the page as it was so that a later init() / initAsync() can try again
    initPromise = null;
    delete window.__HFCHAT_BOOTSTRAP__;
    detachFailedInstance();
    logError(config.logs, "Failed to initialize widget", error);
    throw error;
  });

  // Direct script embeds use the synchronous stub API and may never call
  // initAsync. Handle rejection here without hiding it from awaiters.
  void initPromise.catch(() => {});

  return window.HFChat;
}

/** Resolve with the ready widget, or reject when the load or the init fails. */
export function initAsync(opts?: unknown): Promise<HFChatAPI> {
  if (initPromise) return initPromise;
  const existingApi = getRealApi();
  if (existingApi) return waitForReady(existingApi);
  init(opts);
  if (!initPromise) {
    return Promise.reject(new Error("HFChat initialization did not start"));
  }
  return initPromise;
}

if (typeof window !== "undefined") {
  if (!window.HFChat) {
    window.HFChat = createStubApi();
  }
  if (!window.HumanFirstChat) {
    window.HumanFirstChat = { init, initAsync };
  }
}

if (typeof document !== "undefined" && typeof window !== "undefined") {
  const script = getScriptElement();
  const siteId = script?.getAttribute("data-site-id") ?? undefined;
  const autoInit = script?.getAttribute("data-auto-init") !== "false";

  if (autoInit) {
    const doInit = () => {
      init(siteId ? { siteId } : {});
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", doInit);
    } else {
      setTimeout(doInit, 0);
    }
  }
}

const widgetLoader = { init, initAsync };

export default widgetLoader;
